import type { ReactNode } from "react";
import { useMemo } from "react";
import useHousehold from "src/contexts/household-context";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import GppGoodOutlined from "@mui/icons-material/GppGoodOutlined";
import GppMaybeOutlined from "@mui/icons-material/GppMaybeOutlined";
import HealthAndSafetyOutlined from "@mui/icons-material/HealthAndSafetyOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MedicationOutlined from "@mui/icons-material/MedicationOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { paths } from "src/paths";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

export const useSections = () => {
  const { selectedHousehold } = useHousehold();

  const hOH = selectedHousehold?.head_of_household;
  const estate_plan_foundation = hOH?.estate_plan_foundation;

  const isJoint = (estate_plan_foundation === "joint_trust" || estate_plan_foundation === "attorney");
  const head = selectedHousehold?.head_of_household;

  //------------------------------------------------------------------
  // 🔤  Name helpers
  //------------------------------------------------------------------
  const splitName = (full?: string | null) => {
    if (!full) return { first: "Unknown", last: "" };
    const parts = full.trim().split(/\s+/);
    return { first: parts[0], last: parts[parts.length - 1] };
  };

  const headName   = splitName(head?.full_name);
  const spouseName = splitName(
    (selectedHousehold?.household_member_roles ?? [])
      .find((m) => m.role === "SPOUSE")
      ?.role_target_user_data?.full_name
  );

  // Build the joint label
  let jointTitle: string;
  if (!spouseName.last) {
    // no spouse – fallback
    jointTitle = `${headName.first} ${headName.last}`;
  } else if (
    headName.last.toLowerCase() === spouseName.last.toLowerCase()
  ) {
    // same last name → “Ian & Kanitha Snow”
    jointTitle = `${headName.first} & ${spouseName.first} ${headName.last}`;
  } else {
    // different last names → “Ian & Kanitha”
    jointTitle = `${headName.first} & ${spouseName.first}`;
  }

  return useMemo(
    () => {
      return [
        {
          items: [
            {
              title: "Home",
              path: paths.dashboard.index,
              icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />,
            },
            {
              title: "Sharing",
              path: paths.dashboard.accountSharing("sharing"),
              icon: <PersonAddOutlinedIcon sx={{ fontSize: 18 }} />,
            },
          ],
        },
        {
          subheader: "Essential Documents",
          items: [
            {
              title: "Wills & Trusts",
              icon: <GppGoodOutlined sx={{ fontSize: 18 }} />,
              items: [
                    {
                      title: `${isJoint ? jointTitle : head?.full_name ?? "Unknown"}`,
                      path: `/${selectedHousehold?.head_of_household?.ud_id}/wills-and-trusts`,
                    },
                    ...(isJoint
                      ? [] // skip spouse list
                      : (selectedHousehold?.household_member_roles ?? [])
                          .filter((m) => m.role === "SPOUSE")
                          .map((m) => ({
                            title: m.role_target_user_data?.full_name ?? "Unknown",
                            path: `/${m.role_target_user_data_ud_id}/wills-and-trusts`,
                          }))
                    ),
                  ],
            },            
            {
              title: "Power of Attorney",
              icon: <GppMaybeOutlined sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/financial-power-of-attorney`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/financial-power-of-attorney`,
                })) || [])
              ],
            },
            {
              title: "Health Care Directive",
              icon: <HealthAndSafetyOutlined sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/health-care-directive`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/health-care-directive`,
                })) || [])
              ],
            },
          ],
        },
        {
          subheader: "Total Planning",
          items: [

            {
              title: "Personal",
              icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/personal`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/personal`,
                })) || [])
              ],
            },
            {
              title: "Legal",
              icon: <FolderOutlinedIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/legal`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/legal`,
                })) || [])
              ],
            },
            {
              title: "Medical",
              icon: <MedicationOutlined sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/medical`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/medical`,
                })) || [])
              ],
            },
            {
              title: "Financial",
              icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/financial`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/financial`,
                })) || [])
              ],
            },
            {
              title: "Property",
              icon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/property`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/property`,
                })) || [])
              ],
            },
            {
              title: "Digital",
              icon: <PhoneAndroidOutlinedIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/digital`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/digital`,
                })) || [])
              ],
            },
            {
              title: "Legacy",
              icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 18 }} />,
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/legacy`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/legacy`,
                })) || [])
              ],
            },
          ],
        },
      ];
    },
    [
      selectedHousehold?.household_member_roles, 
      head?.full_name,
      isJoint,
      jointTitle,
      selectedHousehold?.head_of_household?.full_name,
      selectedHousehold?.head_of_household?.ud_id
    ]
  );
};